import { Link, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { movies, categories, getHotMovies, getNewMovies } from '@/mocks/movies';
import type { Movie } from '@/mocks/movies';

function MovieCard({ movie }: { movie: Movie }) {
  return (
    <Link
      to={`/article/${movie.id}`}
      className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200 hover:border-primary-300 transition-all duration-300 cursor-pointer"
    >
      <div className="relative aspect-[2/3] overflow-hidden">
        <img
          src={movie.poster}
          alt={movie.title}
          title={`${movie.title} - 极速影视高清电影大全`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute top-2 right-2 bg-primary-500 text-background-50 text-xs font-bold px-2 py-1 rounded-md whitespace-nowrap">
          {movie.rating}
        </div>
        {movie.isNew && (
          <div className="absolute top-2 left-2 bg-accent-500 text-background-50 text-xs font-bold px-2 py-1 rounded-md whitespace-nowrap">
            新上映
          </div>
        )}
        {movie.isHot && !movie.isNew && (
          <div className="absolute top-2 left-2 bg-primary-500 text-background-50 text-xs font-bold px-2 py-1 rounded-md whitespace-nowrap">
            热门
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
          <span className="text-background-50 text-sm font-medium whitespace-nowrap">点击查看详情</span>
        </div>
      </div>
      <div className="p-3">
        <h3 className="text-sm font-semibold text-foreground-900 truncate mb-1">{movie.title}</h3>
        <div className="flex items-center justify-between text-xs text-foreground-500">
          <span>{movie.categoryName}</span>
          <span>{movie.year}</span>
        </div>
      </div>
    </Link>
  );
}

export default function HomePage() {
  const location = useLocation();
  const [activeCategory, setActiveCategory] = useState('all');
  const hotMovies = getHotMovies();
  const newMovies = getNewMovies();
  const filteredMovies = activeCategory === 'all'
    ? movies
    : movies.filter((m) => m.category === activeCategory);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative w-full h-[500px] md:h-[600px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=dark%20cinematic%20theater%20interior%20with%20warm%20red%20velvet%20curtains%20golden%20stage%20lighting%20dramatic%20atmosphere%20empty%20seats%20luxurious%20movie%20palace%20grand%20architecture%20rich%20warm%20tones%20film%20projector%20light%20beam%20elegant%20composition&width=1920&height=1080&seq=hero-bg-main&orientation=landscape"
          alt="极速影视 - 高清电影在线观看"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>
        <div className="relative z-10 w-full px-4 md:px-6 text-center">
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
            极速影视
          </h1>
          <p className="text-base md:text-lg text-background-200 max-w-2xl mx-auto mb-8 leading-relaxed">
            极速影视高清电影大全，涵盖最新院线大片和热门推荐，全部免费在线观看不缓冲。极速影视最新院线同步更新高清资源，畅享极致观影体验。
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 max-w-lg mx-auto">
            <div className="relative w-full sm:flex-1">
              <input
                type="text"
                placeholder="搜索您想看的电影..."
                className="w-full px-5 py-3.5 text-sm rounded-full bg-background-50/95 border-0 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 transition-all pl-12"
              />
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-lg"></i>
            </div>
            <Link
              to="/category/all"
              className="w-full sm:w-auto px-8 py-3.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer text-center"
            >
              浏览全部
            </Link>
          </div>
          <div className="flex items-center justify-center gap-6 mt-8">
            <div className="text-center">
              <p className="text-2xl md:text-3xl font-bold text-accent-400">{movies.length}+</p>
              <p className="text-xs text-background-300">高清大片</p>
            </div>
            <div className="w-px h-10 bg-background-400/30"></div>
            <div className="text-center">
              <p className="text-2xl md:text-3xl font-bold text-accent-400">{categories.length - 1}</p>
              <p className="text-xs text-background-300">电影分类</p>
            </div>
            <div className="w-px h-10 bg-background-400/30"></div>
            <div className="text-center">
              <p className="text-2xl md:text-3xl font-bold text-accent-400">1080P+</p>
              <p className="text-xs text-background-300">高清画质</p>
            </div>
          </div>
        </div>
      </section>

      {/* Category Tabs */}
      <section className="w-full px-4 md:px-6 py-8 md:py-10 bg-background-50">
        <div className="flex items-center justify-center flex-wrap gap-2">
          {categories.map((cat) => (
            <button
              key={cat.slug}
              onClick={() => setActiveCategory(cat.slug)}
              className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer ${
                activeCategory === cat.slug
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>
      </section>

      {/* Movie Grid */}
      <section className="w-full px-4 md:px-6 py-6 md:py-10 bg-background-100">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
            {activeCategory === 'all' ? '全部电影' : categories.find((c) => c.slug === activeCategory)?.name + '电影'}
          </h2>
          <Link to={`/category/${activeCategory}`} className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap flex items-center gap-1 cursor-pointer">
            查看更多 <i className="ri-arrow-right-line text-xs"></i>
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
          {filteredMovies.slice(0, 12).map((movie) => (
            <MovieCard key={movie.id} movie={movie} />
          ))}
        </div>
      </section>

      {/* Hot Movies */}
      <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
            <i className="ri-fire-line text-primary-500 mr-2"></i>热门推荐
          </h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
          {hotMovies.map((movie) => (
            <MovieCard key={movie.id} movie={movie} />
          ))}
        </div>
      </section>

      {/* CTA Banner */}
      <section className="relative w-full py-16 md:py-20 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=cinematic%20film%20reel%20close%20up%20golden%20and%20warm%20light%20leaking%20through%20curtains%20abstract%20cinema%20texture%20rich%20red%20and%20gold%20gradients%20elegant%20atmosphere&width=1920&height=600&seq=cta-banner&orientation=landscape"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/70"></div>
        <div className="relative z-10 w-full px-4 md:px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-background-50 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
            每日更新 · 高清不缓冲
          </h2>
          <p className="text-sm md:text-base text-background-300 mb-6 max-w-lg mx-auto">
            极速影视每日同步更新最新院线资源，畅享高清不缓冲的极致观影体验
          </p>
          <Link
            to="/category/all"
            className="inline-flex items-center gap-2 px-8 py-3 bg-accent-500 text-background-50 text-sm font-semibold rounded-full hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-play-circle-line text-lg"></i>
            立即观影
          </Link>
        </div>
      </section>

      {/* New Releases */}
      <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
            <i className="ri-sparkling-line text-accent-500 mr-2"></i>最新上映
          </h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
          {newMovies.map((movie) => (
            <MovieCard key={movie.id} movie={movie} />
          ))}
        </div>
      </section>

      {/* Schema.org structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "极速影视",
            "url": "https://www.jisumovies.com/",
            "description": "极速影视高清电影大全涵盖最新院线大片和热门推荐，全部免费在线观看不缓冲，极速影视最新院线同步更新高清资源，畅享极致观影。",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://www.jisumovies.com/search?q={search_term_string}",
              "query-input": "required name=search_term_string"
            },
            "inLanguage": "zh-CN"
          }),
        }}
      />
    </div>
  );
}